import { useRegisterActions } from "kbar";
import { useCallback, useMemo } from "react";
import { useLatest } from "react-use";
import { t } from "ttag";

import { skipToken, useListDatabasesQuery, useSearchQuery } from "metabase/api";
import type { UseInitialCollectionIdProps } from "metabase/common/collections/hooks";
import { useInitialCollectionId } from "metabase/common/collections/hooks";
import { trackMetricCreateStarted } from "metabase/common/data-studio/analytics";
import { canAccessDataStudio } from "metabase/common/data-studio/selectors";
import {
  canUserCreateNativeQueries,
  canUserCreateQueries,
  getUserIsAdmin,
  getUserPersonalCollectionId,
} from "metabase/current-user";
import { getHasDatabaseWithActionsEnabled } from "metabase/databases/utils/predicates";
import { openEmbedJsWizard } from "metabase/embedding/store/embed-setup-modal";
import { PLUGIN_DATA_APPS } from "metabase/plugins";
import { useDispatch, useSelector } from "metabase/redux";
import { openDiagnostics } from "metabase/redux/app";
import type { ModalName } from "metabase/redux/store/modal";
import { closeModal, setOpenModal } from "metabase/redux/ui";
import { useNavigate } from "metabase/router";
import { useColorScheme } from "metabase/ui";
import * as Urls from "metabase/urls";

import {
  type RegisterShortcutProps,
  useRegisterShortcut,
} from "./useRegisterShortcut";

/**
 * Default ordering for the basic actions. kbar ranks matches by search
 * relevance; we use this order only to break ties between equally-relevant
 * matches (e.g. typing "New" matches every "New …" action equally) to keep
 * them in stable order.
 */
export const BASIC_ACTION_ORDER = [
  "create-new-question",
  "create-new-native-query",
  "create-new-research",
  "create-new-dashboard",
  "create-new-document",
  "create-new-collection",
  "create-new-model",
  "create-new-metric",
  "download-diagnostics",
  "navigate-admin-settings",
  "navigate-embed-js",
  "navigate-personal-collection",
  "navigate-user-settings",
  "navigate-trash",
  "navigate-home",
  "navigate-data-studio",
  "navigate-browse-model",
  "navigate-browse-database",
  "navigate-browse-metric",
  "navigate-data-apps",
];

// Small enough that it only orders actions whose relevance scores are equal,
// without ever overriding a meaningfully better match.
const PRIORITY_EPSILON = 0.0001;

const getActionPriority = (id: string) => {
  const index = BASIC_ACTION_ORDER.indexOf(id);
  return index === -1
    ? 0
    : (BASIC_ACTION_ORDER.length - index) * PRIORITY_EPSILON;
};

export const useCommandPaletteBasicActions = ({
  isLoggedIn,
  ...props
}: Pick<UseInitialCollectionIdProps, "location" | "params"> & {
  isLoggedIn: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collectionId = useInitialCollectionId(props) ?? undefined;

  const { data: databasesResponse } = useListDatabasesQuery(
    isLoggedIn ? undefined : skipToken,
  );
  const databases = databasesResponse?.data ?? [];
  const { data: searchResults } = useSearchQuery(
    isLoggedIn
      ? { models: ["dataset"], limit: 1, context: "basic-actions" }
      : skipToken,
  );
  const hasModels = (searchResults?.data?.length ?? 0) > 0;

  const personalCollectionId = useSelector(getUserPersonalCollectionId);
  const isAdmin = useSelector(getUserIsAdmin);
  const hasDataStudioAccess = useSelector(canAccessDataStudio);

  const hasDataAccess = useSelector(canUserCreateQueries);
  const hasNativeWrite = useSelector(canUserCreateNativeQueries);
  const hasDatabaseWithActionsEnabled =
    getHasDatabaseWithActionsEnabled(databases);

  const openNewModal = useCallback(
    (modalId: ModalName) => {
      dispatch(closeModal());
      dispatch(setOpenModal(modalId));
    },
    [dispatch],
  );
  const initialActions = useMemo<RegisterShortcutProps[]>(() => {
    const actions: RegisterShortcutProps[] = [];

    if (hasDataAccess) {
      actions.push({
        id: "create-new-question",
        name: t`New question`,
        section: "basic",
        icon: "insight",
        perform: () => {
          dispatch(closeModal());
          navigate(
            Urls.newQuestion({
              mode: "notebook",
              creationType: "custom_question",
              cardType: "question",
              collectionId,
            }),
          );
        },
      });
    }

    if (hasNativeWrite) {
      actions.push({
        id: "create-new-native-query",
        name: t`New SQL query`,
        section: "basic",
        icon: "sql",
        perform: () => {
          dispatch(closeModal());
          navigate(
            Urls.newQuestion({
              DEPRECATED_RAW_MBQL_type: "native",
              creationType: "native_question",
              cardType: "question",
            }),
          );
        },
      });
    }

    if (hasDataAccess) {
      actions.push({
        id: "create-new-research",
        name: t`New research`,
        section: "basic",
        icon: "telescope",
        perform: () => {
          dispatch(closeModal());
          navigate(Urls.newExploration());
        },
      });
    }

    actions.push({
      id: "create-new-dashboard",
      name: t`New dashboard`,
      section: "basic",
      icon: "dashboard",
      perform: () => {
        openNewModal("dashboard");
      },
    });

    actions.push({
      id: "create-new-document",
      name: t`New document`,
      section: "basic",
      icon: "document",
      perform: () => {
        navigate(Urls.newDocument());
      },
    });

    actions.push({
      id: "create-new-collection",
      name: t`New collection`,
      section: "basic",
      icon: "collection",
      perform: () => {
        openNewModal("collection");
      },
    });

    if (hasNativeWrite) {
      actions.push({
        id: "create-new-model",
        name: t`New model`,
        section: "basic",
        icon: "model",
        perform: () => {
          dispatch(closeModal());
          navigate("/model/new");
        },
      });
    }

    if (hasDataAccess) {
      actions.push({
        id: "create-new-metric",
        name: t`New metric`,
        section: "basic",
        icon: "metric",
        perform: () => {
          trackMetricCreateStarted("command_palette");
          dispatch(closeModal());
          navigate(Urls.newMetric({ collectionId }));
        },
      });
    }

    actions.push({
      id: "download-diagnostics",
      name: t`Download diagnostics`,
      section: "basic",
      icon: "bug",
      keywords: "bug, issue, problem, error, diagnostic",
      shortcut: ["$mod+f1"],
      perform: () => {
        dispatch(openDiagnostics());
      },
    });

    if (isAdmin) {
      actions.push({
        id: "navigate-admin-settings",
        perform: () => navigate("/admin/settings"),
      });
    }

    if (isAdmin) {
      actions.push({
        id: "navigate-embed-js",
        section: "basic",
        icon: "embed",
        keywords: "embed flow, embed js, modular embedding, guest embed",
        perform: () => {
          dispatch(closeModal());
          dispatch(openEmbedJsWizard());
        },
      });
    }

    if (isAdmin && PLUGIN_DATA_APPS.isEnabled) {
      actions.push({
        id: "navigate-data-apps",
        section: "basic",
        icon: "app",
        perform: () => navigate(Urls.dataStudioDataApps()),
      });
    }

    if (personalCollectionId) {
      actions.push({
        id: "navigate-personal-collection",
        perform: () => navigate(`/collection/${personalCollectionId}`),
      });
    }

    actions.push(
      {
        id: "navigate-user-settings",
        section: "basic",
        icon: "person",
        perform: () => navigate("/account/profile"),
      },
      {
        id: "navigate-trash",
        perform: () => navigate("/trash"),
      },
      {
        id: "navigate-home",
        section: "basic",
        icon: "home",
        perform: () => navigate("/"),
      },
    );

    if (hasDataStudioAccess) {
      actions.push({
        id: "navigate-data-studio",
        section: "basic",
        icon: "table",
        perform: () => navigate("/data-studio"),
      });
    }

    const browseActions: RegisterShortcutProps[] = [
      {
        id: "navigate-browse-model",
        name: t`Browse models`,
        section: "basic",
        icon: "model",
        perform: () => {
          navigate("/browse/models");
        },
      },
      {
        id: "navigate-browse-database",
        name: t`Browse databases`,
        section: "basic",
        icon: "database",
        perform: () => {
          navigate("/browse/databases");
        },
      },
      {
        id: "navigate-browse-metric",
        name: t`Browse metrics`,
        section: "basic",
        icon: "metric",
        perform: () => {
          navigate("/browse/metrics");
        },
      },
    ];

    return [...actions, ...browseActions].map((action) => ({
      ...action,
      priority: getActionPriority(action.id),
    }));
  }, [
    dispatch,
    hasDataAccess,
    hasDataStudioAccess,
    hasNativeWrite,
    collectionId,
    openNewModal,
    isAdmin,
    personalCollectionId,
    navigate,
  ]);

  useRegisterShortcut(initialActions, [initialActions]);

  const openActionModal = [];

  if (isAdmin && hasDatabaseWithActionsEnabled && hasNativeWrite && hasModels) {
    openActionModal.push({
      id: "create-action",
      name: t`New action`,
      keywords: t`add action, create action`,
      section: "basic",
      icon: "bolt",
      perform: () => {
        openNewModal("action");
      },
    });
  }
  useRegisterActions(openActionModal, [
    isAdmin,
    hasDatabaseWithActionsEnabled,
    hasNativeWrite,
    hasModels,
  ]);

  const colorSchemeRef = useLatest(useColorScheme());
  useRegisterShortcut([
    {
      id: "toggle-dark-mode",
      perform: () => colorSchemeRef.current.toggleColorScheme(),
    },
    {
      id: "toggle-dark-mode-2",
      perform: () => colorSchemeRef.current.toggleColorScheme(),
    },
  ]);
};
